import { Injectable, inject, signal } from '@angular/core';
import type {
  CustomFieldValueViewModel,
  CustomFieldViewModel,
} from '../models/custom-field.model';
import { CustomFieldsSchemaDataService } from './custom-fields-schema-data.service';

const INITIAL_VALUES_BY_CARD: Record<string, CustomFieldValueViewModel[]> = {
  'card-1': [
    { fieldId: 'f-order-number', value: 'PED-00123' },
    { fieldId: 'f-delivery-date', value: '2026-03-18' },
    { fieldId: 'f-operation-priority', value: 'high' },
    { fieldId: 'f-approvers', value: ['finance', 'ops'] },
    { fieldId: 'f-owner-external', value: 'Maria' },
    { fieldId: 'f-is-blocked', value: false },
    { fieldId: 'f-approved-value', value: 4500 },
    {
      fieldId: 'f-request-notes',
      value: 'Solicitacao vinculada ao ciclo operacional da sprint.',
    },
  ],
};

@Injectable({ providedIn: 'root' })
export class CustomFieldsDataService {
  private readonly schemaService = inject(CustomFieldsSchemaDataService);
  private readonly fieldsByWorkflowSignal = signal<Record<string, CustomFieldViewModel[]>>({});
  private readonly valuesByCardSignal =
    signal<Record<string, CustomFieldValueViewModel[]>>({ ...INITIAL_VALUES_BY_CARD });

  getFieldsByWorkflow(workflowId: string): CustomFieldViewModel[] {
    return (this.fieldsByWorkflowSignal()[workflowId] ?? []).map((item) => ({
      ...item,
      options: item.options.map((option) => ({ ...option })),
    }));
  }

  async ensureWorkflowFieldsLoaded(workflowId: string): Promise<CustomFieldViewModel[]> {
    const cached = this.fieldsByWorkflowSignal()[workflowId];
    if (cached && cached.length > 0) {
      return this.getFieldsByWorkflow(workflowId);
    }
    const response = await this.schemaService.getWorkflowCustomFields(workflowId);
    this.fieldsByWorkflowSignal.update((current) => ({
      ...current,
      [workflowId]: response.fields.map((item) => ({
        ...item,
        options: item.options.map((option) => ({ ...option })),
      })),
    }));
    return this.getFieldsByWorkflow(workflowId);
  }

  async refreshWorkflowFields(workflowId: string): Promise<CustomFieldViewModel[]> {
    if (!workflowId.trim()) return [];
    const response = await this.schemaService.getWorkflowCustomFields(workflowId);
    this.replaceWorkflowFields(workflowId, response.fields);
    return this.getFieldsByWorkflow(workflowId);
  }

  /** GET /api/cards/{id}/detail — `workflowFields` com options em linha. */
  async hydrateWorkflowFieldsFromDetailPayload(
    workflowId: string,
    raw: unknown
  ): Promise<CustomFieldViewModel[]> {
    if (!workflowId.trim()) return [];
    const fields = await this.schemaService.ingestWorkflowFieldsFromDetailPayload(workflowId, raw);
    this.replaceWorkflowFields(workflowId, fields);
    return this.getFieldsByWorkflow(workflowId);
  }

  getValuesByCard(cardId: string): CustomFieldValueViewModel[] {
    const values = this.valuesByCardSignal()[cardId] ?? [];
    return values.map((item) => ({ ...item }));
  }

  setValuesByCard(cardId: string, values: CustomFieldValueViewModel[]): void {
    this.valuesByCardSignal.update((current) => ({
      ...current,
      [cardId]: values.map((item) => ({ ...item })),
    }));
  }

  updateFieldQuickViewVisibility(workflowId: string, fieldId: string, showInQuickView: boolean): void {
    if (!workflowId.trim() || !fieldId.trim()) return;
    this.fieldsByWorkflowSignal.update((current) => {
      const list = current[workflowId] ?? [];
      if (!list.length) return current;
      let changed = false;
      const next = list.map((field) => {
        if (field.fieldId !== fieldId) return field;
        if (field.showInQuickView === showInQuickView) return field;
        changed = true;
        return { ...field, showInQuickView };
      });
      if (!changed) return current;
      return { ...current, [workflowId]: next };
    });
  }

  updateFieldAppVisibility(workflowId: string, fieldId: string, showInApp: boolean): void {
    if (!workflowId.trim() || !fieldId.trim()) return;
    this.fieldsByWorkflowSignal.update((current) => {
      const list = current[workflowId] ?? [];
      if (!list.length) return current;
      let changed = false;
      const next = list.map((field) => {
        if (field.fieldId !== fieldId) return field;
        if (field.showInApp === showInApp) return field;
        changed = true;
        return { ...field, showInApp };
      });
      if (!changed) return current;
      return { ...current, [workflowId]: next };
    });
  }

  replaceWorkflowFields(workflowId: string, fields: CustomFieldViewModel[]): void {
    if (!workflowId.trim()) return;
    this.fieldsByWorkflowSignal.update((current) => ({
      ...current,
      [workflowId]: fields.map((item) => ({
        ...item,
        options: item.options.map((option) => ({ ...option })),
      })),
    }));
  }
}
